import type {
  GetAllDeliverablesDTOType,
  GetAllDeliverablesResponseType,
  GetDeliverableByUserIdDTOType,
  GetDeliverableByUserIdResponseType,
  GetDeliverablePresignedURLDTOType,
  GetDeliverablePresignedURLResponseType,
  GetSessionDTOType,
  GetSessionResponseType,
  GetSessionsDTOType,
  GetSessionsResponseType,
  ReviewSessionDTOType,
  V1AddDeliverableRateDTOType,
  V1AddSessionMenteeReportDTOType,
  V1AddSessionReportDTOType,
  V1AdminExportSessionsDTOType,
  V1AdminExportSessionsResponseType,
  V1ConfirmPresenceDTOType,
  V1CreateSessionDTOType,
  V1CreateSessionResponseType,
  V1ReviewSessionDTOType,
  V1UpdateSessionStatusDTOType,
} from '@/features/sessions/types'
import fetchClient from '@/lib/fetch'

export const V1_SESSIONS_QUERY_KEY = 'v1-sessions'
export const V1_SESSIONS_ADMIN_MANAGER_QUERY_KEY = 'v1-sessions-admin-manager'
export async function getSessions(data: GetSessionsDTOType): Promise<GetSessionsResponseType> {
  let url = `/v2/sessions/mentor-mentee?programId=${data.programId}&page=${data.page}&perPage=${data.perPage}&filterEmail=${data.filterEmail}`

  if (data.filterStatus) {
    url += `&filterStatus=${data.filterStatus}`
  }

  if (data.groupId) {
    url += `&mentorshipGroupId=${data.groupId}`
  }

  return fetchClient(url, {
    method: 'GET',
  })
}

export async function getSessionsAdminManager(data: GetSessionsDTOType): Promise<GetSessionsResponseType> {
  let url = `/v2/sessions/admin-manager?programId=${data.programId}&page=${data.page}&perPage=${data.perPage}&filterEmail=${data.filterEmail}`

  if (data.filterStatus) {
    url += `&filterStatus=${data.filterStatus}`
  }

  if (data.groupId) {
    url += `&mentorshipGroupId=${data.groupId}`
  }

  return fetchClient(url, {
    method: 'GET',
  })
}

export const SESSION_QUERY_KEY = 'v1-session'
export async function getSession(data: GetSessionDTOType): Promise<GetSessionResponseType> {
  return fetchClient(`/v2/sessions/${data.id}`, {
    method: 'GET',
  })
}

export const SESSIONS_BATCH_QUERY_KEY = 'v1-sessions-batch'
export async function getSessionsByIds(ids: string[]): Promise<{ sessions: GetSessionResponseType['session'][] }> {
  return fetchClient(`/v2/sessions/batch?ids=${encodeURIComponent(ids.join(','))}`, {
    method: 'GET',
  })
}

export async function V1AdminExportSessions(
  data: V1AdminExportSessionsDTOType,
): Promise<V1AdminExportSessionsResponseType> {
  return fetchClient(`/v2/sessions/export?programId=${data.programId}&periodId=${data.periodId}`, {
    method: 'GET',
  })
}

export async function V1CreateSessionMutation(data: V1CreateSessionDTOType): Promise<V1CreateSessionResponseType> {
  return fetchClient(`/v2/sessions`, {
    method: 'POST',
    body: JSON.stringify(data),
  })
}

export async function V1UpdateSessionMutation(data: any): Promise<void> {
  return fetchClient(`/v2/sessions/${data.sessionId}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  })
}

export async function V1UpdateSessionStatusMutation(data: V1UpdateSessionStatusDTOType): Promise<void> {
  return fetchClient(`/v2/sessions/${data.sessionId}/status`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  })
}

export async function reviewSessionMutation(data: ReviewSessionDTOType): Promise<void> {
  return fetchClient(`/v2/sessions/${data.sessionId}/review`, {
    method: 'POST',
    body: JSON.stringify(data),
  })
}

export async function V1AddSessionReviewMutation(data: V1ReviewSessionDTOType): Promise<void> {
  return fetchClient(`/v1/user/${data.userId}/session/${data.sessionId}/report`, {
    method: 'POST',
    body: JSON.stringify(data),
  })
}

export async function getDeliverablePresignedURLMutation(
  data: GetDeliverablePresignedURLDTOType,
): Promise<GetDeliverablePresignedURLResponseType> {
  return fetchClient(`/v2/sessions/${data.sessionId}/users/${data.userId}/deliverable`, {
    method: 'POST',
    body: JSON.stringify({ mimetype: data.mimetype, name: data.name }),
  })
}

export const DELIVERABLES_QUERY_KEY = 'deliverables'
export async function getAllDeliverables(data: GetAllDeliverablesDTOType): Promise<GetAllDeliverablesResponseType> {
  return fetchClient(`/v2/sessions/${data.sessionId}/deliverables`, {
    method: 'GET',
  })
}

export const DELIVERABLE_QUERY_KEY = 'deliverable'
export async function getDeliverableByUserId(
  data: GetDeliverableByUserIdDTOType,
): Promise<GetDeliverableByUserIdResponseType> {
  return fetchClient(`/v2/sessions/${data.sessionId}/users/${data.userId}/deliverable`, {
    method: 'GET',
  })
}

export async function V1AddDeliverableRateMutation(data: V1AddDeliverableRateDTOType): Promise<void> {
  return fetchClient(`/v2/sessions/${data.sessionId}/users/${data.userId}/deliverable/rate`, {
    method: 'POST',
    body: JSON.stringify({ rate: data.rate }),
  })
}

export async function skipDeliverableMutation(data: {
  sessionId: string
  userId: string
  skip: boolean
}): Promise<void> {
  return fetchClient(`/v2/sessions/${data.sessionId}/users/${data.userId}/deliverable/skip`, {
    method: 'PATCH',
    body: JSON.stringify({ skip: data.skip }),
  })
}

export async function V1ConfirmPresencesMutation(data: V1ConfirmPresenceDTOType): Promise<void> {
  return fetchClient(`/v2/sessions/presence`, {
    method: 'POST',
    body: JSON.stringify(data),
  })
}

export async function V1AddSessionReportMutation(data: V1AddSessionReportDTOType): Promise<void> {
  return fetchClient(`/v2/sessions/${data.sessionId}/report`, {
    method: 'POST',
    body: JSON.stringify(data),
  })
}

export async function V1AddSessionMenteeReportMutation(data: V1AddSessionMenteeReportDTOType): Promise<void> {
  return fetchClient(`/v2/sessions/${data.sessionId}/mentee-report`, {
    method: 'POST',
    body: JSON.stringify(data),
  })
}
